import { useEffect, useState } from 'react';
import type { ReferenceData, UserSummary } from '../shared/types';
import { api } from './api';
import { AppShell } from './components/AppShell';
import { Toast, type ToastState } from './components/Toast';
import { LoginPage } from './pages/LoginPage';
import { ItemListPage } from './pages/ItemListPage';
import { ItemEditorPage } from './pages/ItemEditorPage';
import { LogsPage } from './pages/LogsPage';
import { CombatAbilityListPage } from './pages/CombatAbilityListPage';
import { CombatAbilityEditorPage } from './pages/CombatAbilityEditorPage';
import { CombatKitListPage } from './pages/CombatKitListPage';
import { CombatKitEditorPage } from './pages/CombatKitEditorPage';
import { GameplayPresetPage } from './pages/GameplayPresetPage';
import { readRoute } from './routing';

export default function App() {
  const [user, setUser] = useState<UserSummary|null>(null);
  const [reference, setReference] = useState<ReferenceData|null>(null);
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState(() => readRoute(location.pathname));
  const [toast, setToast] = useState<ToastState>(null);

  useEffect(() => {
    api.me().then((response) => setUser(response.user)).catch(() => setUser(null)).finally(() => setLoading(false));
    const handler = () => setRoute(readRoute(location.pathname));
    addEventListener('popstate', handler);
    return () => removeEventListener('popstate', handler);
  }, []);

  useEffect(() => {
    if (user && !reference) {
      api.reference().then(setReference).catch((error) => show('error', error instanceof Error ? error.message : 'Referenzdaten konnten nicht geladen werden.'));
    }
  }, [user]);

  useEffect(() => {
    const runtimeError = (event: ErrorEvent) => {
      void api.logClient('error', 'client.runtime_error', event.message || 'Unbehandelter JavaScript-Fehler.', {
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error instanceof Error ? event.error.stack : null,
      });
    };
    const rejected = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      void api.logClient('error', 'client.unhandled_rejection', reason instanceof Error ? reason.message : 'Unbehandeltes Promise.', {
        reason: reason instanceof Error ? { name: reason.name, message: reason.message, stack: reason.stack } : String(reason),
      });
    };
    addEventListener('error', runtimeError);
    addEventListener('unhandledrejection', rejected);
    return () => {
      removeEventListener('error', runtimeError);
      removeEventListener('unhandledrejection', rejected);
    };
  }, []);

  function nav(path: string) {
    history.pushState({}, '', path);
    setRoute(readRoute(path));
  }

  function show(kind: 'success'|'error', message: string) {
    setToast({ kind, message });
    if (kind === 'error') void api.logClient('error', 'client.error_message', message, { route: location.pathname });
    setTimeout(() => setToast(null), 7000);
  }

  async function logout() {
    try { await api.logout(); }
    finally {
      setUser(null);
      setReference(null);
      history.replaceState({}, '', '/login');
    }
  }

  if (loading) return <div className="splash"><img src="/assets/logo-mark.svg"/><span>MYY Content Studio wird geladen …</span></div>;
  if (!user) return <LoginPage onLogin={(nextUser) => {
    setUser(nextUser);
    history.replaceState({}, '', '/items');
    setRoute({ page: 'list', id: null, duplicateFromId: null });
  }}/>;
  if (!reference) return <div className="splash"><img src="/assets/logo-mark.svg"/><span>Katalog wird vorbereitet …</span></div>;

  const active = route.page === 'logs' ? 'logs' : route.page === 'presets' ? 'presets' : route.page === 'abilities' || route.page === 'ability-editor' ? 'abilities' : route.page === 'kits' || route.page === 'kit-editor' ? 'kits' : 'items';

  return <AppShell
    user={user}
    onLogout={logout}
    onList={() => nav('/items')}
    onAbilities={() => nav('/abilities')}
    onKits={() => nav('/kits')}
    onPresets={() => nav('/gameplay-presets')}
    onLogs={() => nav('/logs')}
    active={active}
  >
    {route.page === 'list' && <ItemListPage
      reference={reference}
      onOpen={(id) => nav(`/items/${id}`)}
      onNew={() => nav('/items/new')}
      onDuplicate={(id) => nav(`/items/duplicate/${encodeURIComponent(id)}`)}
      onMessage={show}
    />}
    {route.page === 'editor' && <ItemEditorPage
      id={route.id}
      duplicateFromId={route.duplicateFromId}
      reference={reference}
      onBack={() => nav('/items')}
      onDuplicate={(id) => nav(`/items/duplicate/${encodeURIComponent(id)}`)}
      onSaved={(id) => { if (!route.id) nav(`/items/${id}`); }}
      onDeleted={() => nav('/items')}
      onMessage={show}
    />}
    {route.page === 'abilities' && <CombatAbilityListPage onOpen={(id) => nav(`/abilities/${id}`)} onNew={() => nav('/abilities/new')} onMessage={show}/>}
    {route.page === 'ability-editor' && <CombatAbilityEditorPage id={route.id} reference={reference} onBack={() => nav('/abilities')} onSaved={(id) => { if (!route.id) nav(`/abilities/${id}`); }} onMessage={show}/>}
    {route.page === 'kits' && <CombatKitListPage onOpen={(id) => nav(`/kits/${id}`)} onNew={() => nav('/kits/new')} onMessage={show}/>}
    {route.page === 'kit-editor' && <CombatKitEditorPage id={route.id} onBack={() => nav('/kits')} onSaved={(id) => { if (!route.id) nav(`/kits/${id}`); }} onMessage={show}/>}
    {route.page === 'presets' && <GameplayPresetPage onMessage={show} onReferenceChanged={setReference}/>}
    {route.page === 'logs' && <LogsPage onMessage={show}/>}
    <Toast toast={toast} onClose={() => setToast(null)}/>
  </AppShell>;
}
